/**
 * Contract Review checklist — NTES-QSF-SAL-02 (mirrors BE contract-review-sections.ts).
 */

export type ContractReviewChecklistItem = {
  label: string;
  response?: string;
  remarks?: string;
};

export const CONTRACT_REVIEW_SECTION_KEYS = ['review_of_order', 'review_of_amendments'] as const;

export type ContractReviewSectionKey = (typeof CONTRACT_REVIEW_SECTION_KEYS)[number];

export const CR_REVIEW_OF_ORDER_ITEM_LABELS = [
  'Are the initial review points identified at enquiry time re-reviewed and still acceptable by the time of receipt of the Customer order or PO?',
  'Is the Purchase Order in line with the submitted quotation/offer/proposal? If no, mention the deviation. In case of commencement of work prior to PO release using an approval form, update this section on receipt of the customer PO.',
  'Is the above deviation clarified with the customer?',
  'Is a revised quotation sent for the above identified deviation? If yes, mention the revised quotation number and revision (in remarks).',
  'Are the deviations communicated to concerned personnel (internally, the customer, and other relevant interested parties)?',
  'Is the revised quotation accepted by the customer and a revised PO / amendment to PO issued by the customer?',
  'Is there any impact on the existing production? Detail the changes to be made internally and notify the customer if required by contract (in remarks).',
  'Is there any impact on the existing delivery schedule? Detail the changes (in remarks).',
  'Are the delivery changes communicated to the customer and is the new delivery timeline accepted by the customer?',
] as const;

export const CR_REVIEW_OF_AMENDMENTS_ITEM_LABELS = [
  'Are there any changes in the PO/Contract? If yes, describe the change (in remarks).',
  'Are the new changes reviewed by relevant personnel?',
  'Are the new changes accepted by relevant personnel?',
  'Are the relevant documents amended and communicated to concerned personnel?',
] as const;

export const CONTRACT_REVIEW_SECTION_ITEM_LABELS: Record<ContractReviewSectionKey, readonly string[]> = {
  review_of_order: CR_REVIEW_OF_ORDER_ITEM_LABELS,
  review_of_amendments: CR_REVIEW_OF_AMENDMENTS_ITEM_LABELS,
};

export const CONTRACT_REVIEW_SECTION_TITLES: Record<string, string> = {
  review_of_order: 'Review of Order',
  review_of_amendments: 'Review of Amendments',
  commercial: 'Commercial',
  scope: 'Scope',
  technical: 'Technical',
  delivery: 'Delivery',
  quality: 'Quality',
  legal: 'Legal',
  risk: 'Risk',
  final: 'Final',
};

function asChecklist(labels: readonly string[]): ContractReviewChecklistItem[] {
  return labels.map((label) => ({ label, response: 'N/A', remarks: '' }));
}

export function buildDefaultContractReviewSections(): Record<string, ContractReviewChecklistItem[]> {
  return {
    review_of_order: asChecklist(CR_REVIEW_OF_ORDER_ITEM_LABELS),
  };
}

export function buildAmendmentsSectionDefaults(): ContractReviewChecklistItem[] {
  return asChecklist(CR_REVIEW_OF_AMENDMENTS_ITEM_LABELS);
}

export function isQsfContractReviewSections(sections: unknown): boolean {
  if (!sections || typeof sections !== 'object' || Array.isArray(sections)) return false;
  return Array.isArray((sections as Record<string, unknown>).review_of_order);
}

export function hasAmendmentsSection(sections: unknown): boolean {
  if (!sections || typeof sections !== 'object' || Array.isArray(sections)) return false;
  return Array.isArray((sections as Record<string, unknown>).review_of_amendments);
}

function mapChecklistArray(chunk: unknown[]): ContractReviewChecklistItem[] {
  return chunk.map((item: any) => ({
    label: String(item?.label || ''),
    response: String(item?.response ?? 'N/A'),
    remarks: String(item?.remarks || ''),
  }));
}

function alignToLabels(
  labels: readonly string[],
  chunk: ContractReviewChecklistItem[]
): ContractReviewChecklistItem[] {
  return labels.map((label, idx) => {
    const existing = chunk.find((c) => String(c.label || '').trim() === label) || chunk[idx] || {};
    return {
      label,
      response: String((existing as ContractReviewChecklistItem).response || 'N/A'),
      remarks: String((existing as ContractReviewChecklistItem).remarks || ''),
    };
  });
}

export function normalizeContractReviewSections(
  raw: Record<string, unknown> | null | undefined
): Record<string, ContractReviewChecklistItem[]> {
  if (!raw || typeof raw !== 'object') return buildDefaultContractReviewSections();

  if (!isQsfContractReviewSections(raw)) {
    const out: Record<string, ContractReviewChecklistItem[]> = {};
    for (const [key, chunk] of Object.entries(raw)) {
      if (!Array.isArray(chunk)) continue;
      out[key] = mapChecklistArray(chunk);
    }
    return Object.keys(out).length ? out : buildDefaultContractReviewSections();
  }

  const out: Record<string, ContractReviewChecklistItem[]> = {
    review_of_order: alignToLabels(
      CR_REVIEW_OF_ORDER_ITEM_LABELS,
      Array.isArray(raw.review_of_order) ? mapChecklistArray(raw.review_of_order as unknown[]) : []
    ),
  };
  if (Array.isArray(raw.review_of_amendments)) {
    out.review_of_amendments = alignToLabels(
      CR_REVIEW_OF_AMENDMENTS_ITEM_LABELS,
      mapChecklistArray(raw.review_of_amendments as unknown[])
    );
  }
  return out;
}

/** Visible tab keys for a normalized sections object (legacy or QSF). */
export function getContractReviewVisibleSectionKeys(
  sections: Record<string, ContractReviewChecklistItem[]> | null | undefined,
  amendmentsApplicable?: boolean
): string[] {
  if (!sections || typeof sections !== 'object') return ['review_of_order'];
  if (isQsfContractReviewSections(sections)) {
    const keys = ['review_of_order'];
    if (amendmentsApplicable || hasAmendmentsSection(sections)) {
      keys.push('review_of_amendments');
    }
    return keys;
  }
  return Object.keys(sections).filter((k) => Array.isArray(sections[k]));
}

export function mergeSectionsPreservingLabels(
  currentSections: Record<string, ContractReviewChecklistItem[]>,
  nextSections: Record<string, ContractReviewChecklistItem[]>,
  options?: { includeAmendments?: boolean }
): Record<string, ContractReviewChecklistItem[]> {
    const includeAmendments =
      options?.includeAmendments ??
      (hasAmendmentsSection(nextSections) || hasAmendmentsSection(currentSections));

  if (isQsfContractReviewSections(currentSections) || isQsfContractReviewSections(nextSections)) {
    const orderLabels = CR_REVIEW_OF_ORDER_ITEM_LABELS;
    const currentOrder = Array.isArray(currentSections?.review_of_order) ? currentSections.review_of_order : [];
    const nextOrder = Array.isArray(nextSections?.review_of_order) ? nextSections.review_of_order : [];
    const merged: Record<string, ContractReviewChecklistItem[]> = {
      review_of_order: orderLabels.map((label, idx) => {
        const c = currentOrder.find((x) => x.label === label) || currentOrder[idx] || {};
        const n = nextOrder.find((x) => x.label === label) || nextOrder[idx] || {};
        return {
          label,
          response: String(n.response ?? c.response ?? 'N/A'),
          remarks: String(n.remarks ?? c.remarks ?? ''),
        };
      }),
    };
    if (includeAmendments) {
      const amendLabels = CR_REVIEW_OF_AMENDMENTS_ITEM_LABELS;
      const currentAmend = Array.isArray(currentSections?.review_of_amendments)
        ? currentSections.review_of_amendments
        : buildAmendmentsSectionDefaults();
      const nextAmend = Array.isArray(nextSections?.review_of_amendments)
        ? nextSections.review_of_amendments
        : currentAmend;
      merged.review_of_amendments = amendLabels.map((label, idx) => {
        const c = currentAmend.find((x) => x.label === label) || currentAmend[idx] || {};
        const n = nextAmend.find((x) => x.label === label) || nextAmend[idx] || {};
        return {
          label,
          response: String(n.response ?? c.response ?? 'N/A'),
          remarks: String(n.remarks ?? c.remarks ?? ''),
        };
      });
    }
    return merged;
  }

  // Legacy: merge by existing keys
  const keys = Array.from(
    new Set([...Object.keys(currentSections || {}), ...Object.keys(nextSections || {})])
  );
  const merged: Record<string, ContractReviewChecklistItem[]> = {};
  for (const key of keys) {
    const current = Array.isArray(currentSections?.[key]) ? currentSections[key] : [];
    const next = Array.isArray(nextSections?.[key]) ? nextSections[key] : [];
    const len = Math.max(current.length, next.length);
    merged[key] = Array.from({ length: len }, (_, idx) => {
      const c = current[idx] || {};
      const n = next[idx] || {};
      return {
        label: String(n.label ?? c.label ?? ''),
        response: String(n.response ?? c.response ?? 'N/A'),
        remarks: String(n.remarks ?? c.remarks ?? ''),
      };
    });
  }
  return merged;
}

export function isCompleteResponse(response: unknown): boolean {
  const r = String(response ?? '')
    .trim()
    .toLowerCase();
  return r === 'yes' || r === 'n/a' || r === 'na';
}

export function sectionProgress(items: unknown): { complete: number; total: number } {
  const list = Array.isArray(items) ? items : [];
  return {
    total: list.length,
    complete: list.filter((item) => isCompleteResponse((item as ContractReviewChecklistItem)?.response)).length,
  };
}

export function getDefaultContractReviewItemLabel(sectionKey: string, index: number): string {
  const labels = CONTRACT_REVIEW_SECTION_ITEM_LABELS[sectionKey as ContractReviewSectionKey];
  if (!labels || index < 0 || index >= labels.length) return '';
  return labels[index] || '';
}

export function formatContractReviewSectionTitle(key: string): string {
  return (
    CONTRACT_REVIEW_SECTION_TITLES[key] ||
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
